import type { Category } from '@aspenware-library/types'
interface CategoryGetters<CATEGORY> {
  /**
   * Takes a category tree and flattens it, filtering for categories that should be included in the top menu and aren't hidden
   * @param categories
   * @returns flattened categories that should be included in the top menu
   */
  getTopMenuCategories(categories: CATEGORY[]): CATEGORY[]
  getCategories(categories: CATEGORY[]): CATEGORY[]
  getCategoryName(category?: CATEGORY): string
  getCategorySlug(category?: CATEGORY): string
  getDescription(category?: CATEGORY): string | undefined
  getCategoryLink(opts: { category?: CATEGORY }): string
  /**
   * Determines if a category is to be navigated to via vue-router or not.
   * @param category
   * @returns true if the category should be navigated to via vue-router, false otherwise
   */
  shouldCategoryUseRouter(category: Category): boolean
  shouldUseExternalRoute(currentRoute: string): boolean
}

function getCategoryName(category: Category): string {
  return category?.Name || 'Missing category name'
}

function getCategorySlug(category: Category): string {
  return category?.Route || '/missing-category-slug'
}

function getCategoryPath(category: Category): string {
  const prefix = category.UseLegacy ? 's' : 'l'
  return category.ParentRoute
    ? `/${prefix}${category.ParentRoute}${getCategorySlug(category)}`
    : `/${prefix}${getCategorySlug(category)}`
}

/**
 * Determines if a category is to be navigated to via vue-router or not.
 * @param category
 * @returns true if the category should be navigated to via vue-router, false otherwise
 */
function shouldCategoryUseRouter(category: Category): boolean {
  return category.UseLegacy && !category.IsExternalUrl
}

function getCategories(categories: Category[]): Category[] {
  // categorise are presorted by the server
  return categories.filter((category) => !category.Hidden)
}

function getTopMenuCategories(categories: Category[]): Category[] {
  return categories.filter((category) => !category.Hidden && category.IncludeInTopMenu)
}

function getDescription(category: Category): string | undefined {
  return category?.Description || undefined
}

function getCategoryLink(opts: { category?: Category }): string {
  const { category } = opts

  if (!category) return ''

  if (category.IsExternalUrl) {
    return category.ExternalUrl as string
  }

  return getCategoryPath(category)
}

function shouldUseExternalRoute(currentRoute: string): boolean {
  const externalCategories = [
    '/cart',
    '/customer',
    '/linkedproducts',
    '/loyaltyaccount',
    '/order',
    '/paymentplans',
    '/vouchers',
    '/waiver',
    '/renewal',
  ]
  return externalCategories.some((route) => currentRoute.toLowerCase().startsWith(route.toLowerCase()))
}
export const categoryGetters: CategoryGetters<Category> = {
  getCategoryName,
  getCategorySlug,
  getCategories,
  getTopMenuCategories,
  getDescription,
  getCategoryLink,
  shouldCategoryUseRouter,
  shouldUseExternalRoute,
}
